import { assignIn, debounce, pick } from 'lodash-es'
import { v4 as guid } from 'uuid'
import { fabric } from 'fabric'
import { whiteboardConstants } from '@/models/constants'
import { useUserStore } from '@/store/userData'
import appConfig from '@/services/appConfig'
import { useWhiteboardStore } from '@/store/whiteboard'

export interface IWbDunesImageOptions extends fabric.IImageOptions, IWbObjectProps {
  id?: string
  lock?: boolean
  preventUnlock?: boolean
  preventDelete?: boolean
  actualWidth: number
  actualHeight: number
  assetId: string
  imageSet: string
  lastResolution?: number
  isUpdatingImage?: boolean
  displayScaleX?: number
  displayScaleY?: number
  lastResolutionWidth?: number
  lastResolutionHeight?: number
}

export default class WbDunesImage extends fabric.Image implements IWbObject {
  public id: string
  public type = whiteboardConstants.objectTypes.dunesImage
  public lock: boolean
  public preventUnlock: boolean
  public connectable: boolean
  public localOnly?: boolean | undefined
  public excludeFromGroupSelection?: boolean | undefined
  public preventDelete?: boolean
  public canBeLinkedTo = true
  public canLink = false
  public link = undefined
  public actualWidth: number
  public actualHeight: number
  public assetId: string
  public imageSet: string
  public lastResolution: number
  public isUpdatingImage: boolean
  public displayScaleX?: number
  public displayScaleY?: number
  public lastResolutionWidth: number
  public lastResolutionHeight: number

  public editableProps: Record<string, IWbObjectProp> = {
    scale: { name: 'size', type: 'scale', editable: true },
    lock: { name: 'Lock', type: 'lock', editable: true, editableWhenLocked: true },
    info: { name: 'info', type: 'info', editable: true, editableWhenLocked: true },
  }

  public actions: Record<string, IWObjectActions> = {
    selectSimilar: { action: 'selectSimilar', label: 'Select Similar', faicon: 'fa-light fa-check-double', showInSubMenu: true },
    bringFront: { action: 'bringFront', label: 'Bring to Front', faicon: 'fa-light fa-bring-front', showInSubMenu: true },
    sendBack: { action: 'sendBack', label: 'Send to Back', faicon: 'fa-light fa-send-back', showInSubMenu: true },
    // group: { action: 'group', label: 'Group', faicon: 'fa-light fa-object-group', showInSubMenu: true, multiple: true },
    cloneAsImage: { action: 'cloneAsImage', label: 'Copy as Image', faicon: 'fa-light fa-clone', showInSubMenu: true },
    crop: { action: 'cropImage', label: 'Crop Image', faicon: 'fa-light fa-crop', showInSubMenu: true },
    delete: { action: 'delete', label: 'Remove', faicon: 'fa-light fa-trash-can', showInSubMenu: true },
  }

  constructor(e: HTMLImageElement, opt?: IWbDunesImageOptions) {
    super(e, opt)
    this.id = opt?.id || guid()

    this.lock = opt?.lock || false
    this.connectable = true
    this.preventUnlock = opt?.preventUnlock || false
    this.localOnly = opt?.localOnly || false
    this.excludeFromGroupSelection = opt?.excludeFromGroupSelection || false
    this.preventDelete = opt?.preventDelete || false
    this.actualWidth = opt?.actualWidth || 0
    this.actualHeight = opt?.actualHeight || 0
    this.setLock(this.lock)
    this.objectCaching = false
    this.statefullCache = false
    this.lastResolution = opt?.lastResolution || e.width
    this.lastResolutionWidth = opt?.lastResolutionWidth || e.width
    this.lastResolutionHeight = opt?.lastResolutionHeight || e.height
    this.noScaleCache = true
    this.needsItsOwnCache = () => false
    this.isUpdatingImage = false
    this.assetId = opt?.assetId || ''
    this.imageSet = opt?.imageSet || ''
    this.displayScaleX = this.actualWidth / this.actualHeight
    this.displayScaleY = this.actualHeight / this.actualWidth
    this.stateProperties = this.stateProperties?.concat(['lock'])
    this.on('scaling', () => {
      const debouncedCheckAndReplace = debounce(() => {
        if (this.isUpdatingImage) { return }
        this.applyScaling()
      }, 2000)
      if (!this.isUpdatingImage) {
        debouncedCheckAndReplace()
      }
    })
  }

  applyScaling(notifyObjectModified: boolean = true) {
    const aspectRatio = this.actualWidth / this.actualHeight
    const displayWidth = this.width! * this.scaleX!
    const displayHeight = this.height! * this.scaleY!
    const currentSize = Math.max(displayWidth, displayHeight)
    const newResolution = WbDunesImage.getNearestResolution(currentSize, Math.max(this.actualWidth, this.actualHeight))
    if (newResolution !== this.lastResolution && (newResolution <= this.actualWidth || newResolution <= this.actualHeight)) {
      let newImageWidth = Math.round(displayWidth)
      let newImageHeight = Math.round(newResolution / aspectRatio)
      let loadWidth = newResolution
      let loadHeight = newImageHeight
      if (this.actualHeight > this.actualWidth) {
        newImageHeight = Math.round(displayHeight)
        newImageWidth = Math.round(newResolution / (this.actualHeight / this.actualWidth))
        loadWidth = newImageWidth
        loadHeight = newResolution
      }

      const cropRatioX = (this.cropX ?? 0) / this.lastResolutionWidth
      const cropRatioY = (this.cropY ?? 0) / this.lastResolutionHeight
      const cropRatioW = this.width! / this.lastResolutionWidth
      const cropRatioH = this.height! / this.lastResolutionHeight

      this.isUpdatingImage = true
      fabric.util.loadImage(WbDunesImage.getImageUrl(loadWidth, loadHeight, this.imageSet), (newImgEl) => {
        if (newImgEl) {
          // const nativeW = newImgEl.naturalWidth
          // const nativeH = newImgEl.naturalHeight
          this.setElement(newImgEl)
        }
        const cropX = cropRatioX * loadWidth
        const cropY = cropRatioY * loadHeight
        const cropW = cropRatioW * loadWidth
        const cropH = cropRatioH * loadHeight

        const newScaleX = displayWidth / cropW
        const newScaleY = displayHeight / cropH

        this.set({
          width: cropW,
          height: cropH,
          cropX,
          cropY,
          scaleX: newScaleX,
          scaleY: newScaleY,
          displayScaleX: (loadWidth * newScaleX) / (this.actualWidth || 1),
          displayScaleY: (loadHeight * newScaleY) / (this.actualHeight || 1),
        } as unknown as Partial<this>)

        this.lastResolution = newResolution
        this.lastResolutionWidth = loadWidth
        this.lastResolutionHeight = loadHeight
        this.isUpdatingImage = false
        this.canvas?.requestRenderAll()
        if (notifyObjectModified) {
          this.canvas?.fire('object:modified', { target: this })
        }
      }, null, 'anonymous')
    }
  }

  getDisplayScale() {
    const displayWidth = this.width! * this.scaleX!
    const displayHeight = this.height! * this.scaleY!
    this.displayScaleX = displayWidth / this.actualWidth
    this.displayScaleY = displayHeight / this.actualHeight
    return Math.max(this.displayScaleX, this.displayScaleY)
  }

  setProp(prop: string, value: any, ignoreHistory: boolean = false, notifyObjectModified: boolean = true) {
    switch (prop) {
      case 'scale':
        if (value.scale <= 100 && this.width && this.height && this.actualWidth && this.actualHeight) {
          const displayScaleX = value.scaleX ? value.scaleX / 100 : value.scale / 100
          const displayScaleY = value.scaleY ? value.scaleY / 100 : value.scale / 100
          const scaleX = (this.actualWidth * displayScaleX) / this.width
          const scaleY = (this.actualHeight * displayScaleY) / this.height
          this.set('displayScaleX', displayScaleX)
          this.set('displayScaleY', displayScaleY)

          // // Apply scaling based on current image element size
          this.set('scaleX', scaleX)
          this.set('scaleY', scaleY)
          this.applyScaling()
        }
        break
      case 'lock':
        this.set('lock', value.lock)
        this.setLock(value.lock)
        break
      default:
        console.warn('Attempting to set unsupported WbObjectProp', prop, value)
        return
    }
    this.dirty = true
    this.canvas?.requestRenderAll()
    if (notifyObjectModified) {
      this.canvas?.fire('object:modified', { target: this, ignoreHistory })
    }
  }

  getProp(prop: string) {
    const result: any = {}
    switch (prop) {
      case 'scale':
        result.scale = Math.round(this.getDisplayScale() * 100)
        break
      case 'lock':
        result.lock = this.lock
        break
      default:
        console.warn('Attempting to get unsupported WbObjectProp', prop)
    }
    return result
  }

  setLock(lock: boolean) {
    this.set('lockMovementX', lock)
    this.set('lockMovementY', lock)
    this.set('lockRotation', lock)
    this.set('lockScalingFlip', lock)
    this.set('lockScalingX', lock)
    this.set('lockScalingY', lock)
    this.set('hasControls', !lock)
  }

  override toObject() {
    const propsToPluck = [
      'id',
      'type',
      'top',
      'left',
      'width',
      'height',
      'angle',
      'scaleX',
      'scaleY',
      'lock',
      'version',
      'localOnly',
      'preventUnlock',
      'cropX',
      'cropY',
      'actualWidth',
      'lastResolutionHeight',
      'lastResolutionWidth',
      'actualHeight',
      'assetId',
      'imageSet',
    ]
    return { ...pick(this, propsToPluck) }
  }

  enableCropping() {
    if (this.canvas) {
      this.canvas.uniformScaling = false
    }
    // Create the cropping rectangle (resizable)
    const cropRect = new fabric.Rect({
      top: this.top,
      left: this.left,
      angle: this.angle,
      width: this.getScaledWidth(),
      height: this.getScaledHeight(),
      stroke: 'blue',
      strokeWidth: 2,
      strokeDashArray: [5, 5],
      fill: 'rgba(0,0,0,0.3)',
      lockRotation: true,
      lockUniScaling: false,
    })
    const cropX = this.cropX || 0
    const cropY = this.cropY || 0
    const width = this.width || 0
    const height = this.height || 0

    const originalSize = this.getOriginalSize()
    this.set({
      cropX: null,
      cropY: null,
      left: this.left! - cropX * this.scaleX!,
      top: this.top! - cropY * this.scaleY!,
      width: originalSize.width,
      height: originalSize.height,
      dirty: false,
    } as unknown as Partial<this>)

    cropRect.set({
      left: this.left! + cropX! * this.scaleX!,
      top: this.top! + cropY! * this.scaleY!,
      width: width * this.scaleX!,
      height: height * this.scaleY!,
      dirty: false,
    })

    this.selectable = false
    this.canvas?.add(cropRect)
    this.canvas?.discardActiveObject()
    this.canvas?.setActiveObject(cropRect)
    this.canvas?.requestRenderAll()

    cropRect.on('moving', () => {
      // Image boundaries
      const imgLeft = this.left
      const imgTop = this.top
      const imgRight = imgLeft! + this.width! * this.scaleX!
      const imgBottom = imgTop! + this.height! * this.scaleY!

      // Restrict movement within the image
      cropRect.left = Math.max(imgLeft!, Math.min(cropRect.left!, imgRight - cropRect.width! * cropRect.scaleX!))
      cropRect.top = Math.max(imgTop!, Math.min(cropRect.top!, imgBottom - cropRect.height! * cropRect.scaleY!))
    })

    cropRect.on('scaling', () => {
      const cropWidth = cropRect.width! * cropRect.scaleX!
      const cropHeight = cropRect.height! * cropRect.scaleY!
      // Image boundaries
      const imgLeft = this.left || 0
      const imgTop = this.top || 0
      const imgRight = imgLeft + (this.width! * this.scaleX!)
      const imgBottom = imgTop + (this.height! * this.scaleY!)
      if (this.left! > cropRect.left!) {
        cropRect.left = this.left
      }
      if (this.top! > cropRect.top!) {
        cropRect.top = this.top
      }
      // Restrict scaling within the image
      if (cropRect.left! + cropWidth > imgRight) {
        cropRect.scaleX = (imgRight - cropRect.left!) / cropRect.width!
      }
      if (cropRect.top! + cropHeight > imgBottom) {
        cropRect.scaleY = (imgBottom - cropRect.top!) / cropRect.height!
      }

      // Prevent shrinking below a minimum size
      cropRect.scaleX = Math.max(cropRect.scaleX!, 0.1)
      cropRect.scaleY = Math.max(cropRect.scaleY!, 0.1)
    })

    cropRect.on('deselected', () => {
      this.canvas?.remove(cropRect)
      const cropX = (cropRect.left! - this.left!) / this.scaleX!
      const cropY = (cropRect.top! - this.top!) / this.scaleY!
      const width = (cropRect.width! * cropRect.scaleX!) / this.scaleX!
      const height = (cropRect.height! * cropRect.scaleY!) / this.scaleY!

      // crop
      this.set({
        cropX,
        cropY,
        width,
        height,
        top: this.top! + cropY * this.scaleY!,
        left: this.left! + cropX * this.scaleX!,
        selectable: true,
        // cropped: 1,
      } as unknown as Partial<this>)
      this.selectable = true
      this.canvas?.requestRenderAll()
      this.canvas?.fire('object:modified', { target: this })

      if (this.canvas) {
        this.canvas.uniformScaling = true
      }
    })
  }

  static fromObject(object: WbDunesImage, callback?: Function) {
    this.loadFromUrl(object).then((v) => { callback && callback(v) })
  }

  static getImageUrl(w: number, h: number, imageSet: string) {
    const params = new URLSearchParams()
    const userStore = useUserStore()
    const width = Math.round(w)
    const height = Math.round(h)
    params.set('Context', userStore.activeCatalog!.DuneContext)
    params.set('ContextKey', userStore.activeCatalog!.ContextKey)
    params.set('ImageSet', imageSet)
    params.set('w', width.toString())
    params.set('h', height.toString())
    params.set('trim', 'false')
    params.set('f', 'webp')
    return `${appConfig.AssetsUrl}/assets/r?${params.toString()}`
  }

  static loadFromUrl(opt: IWbDunesImageOptions) {
    const whiteboardStore = useWhiteboardStore()
    let imageSet = opt.imageSet
    if (!opt.imageSet && whiteboardStore.currentBoardId && opt.id) {
      imageSet = `${appConfig.T1Env}|${whiteboardStore.currentBoardId.toString()}|${opt.id.toString()}`
    }
    const url = WbDunesImage.getImageUrl(opt.lastResolutionWidth ? opt.lastResolutionWidth : opt.width!, opt.lastResolutionHeight ? opt.lastResolutionHeight : opt.height!, imageSet)
    opt.lastResolution = Math.max(opt.lastResolutionWidth ? opt.lastResolutionWidth : opt.width!, opt.lastResolutionHeight ? opt.lastResolutionHeight : opt.height!)
    opt.imageSet = imageSet
    return new Promise((resolve, reject) => {
      fabric.util.loadImage(url, (oImg) => {
        const img = new Image()
        const options = assignIn({ top: 10 + img.height / 2, left: 10 + img.width / 2 }, opt)
        img.onload = () => {
          resolve(new WbDunesImage(oImg, options))
        }
        img.onerror = (err) => {
          console.error('Error loading image', url, err)
          resolve(new WbDunesImage(oImg, options))
          // reject(err)
        }
        img.src = url
      }, null, 'anonymous')
    })
  }

  static getNearestResolution(size: number, actualSize: number) {
    const userStore = useUserStore()
    const resolutions = userStore.activeCatalog?.Config.ImageDunesSizesList || [200, 400, 800, 1200, 1600, 2000]
    if (actualSize > resolutions[resolutions.length - 1]) {
      resolutions.push(actualSize)
    }
    const res = resolutions
    let closest = res[0]
    let minDiff = Math.abs(size - closest)

    for (let i = 1; i < res.length; i++) {
      const diff = Math.abs(size - res[i])
      if (diff < minDiff) {
        minDiff = diff
        closest = res[i]
      }
    }
    // If the suggested resolution is higher than the actual image, fallback to actual
    if (closest > actualSize) {
      return actualSize
    }
    return closest
  }
}

const f: any = fabric
f.WbDunesImage = WbDunesImage
